import { useMemo, useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useFormik } from "formik";
import * as Yup from "yup";
import Select from "react-select";
import { getSellerProductsQuery, getProductsQuery, getSellerAddressesQuery } from "../../queries";
import Loader from "../Common/Loader";
import Link from "react-router-dom";
import TableContainer from "../Common/TableContainer";
import DeleteModal from "../Common/DeleteModal";
import { addProductPrice, editProductPrice, deleteProductPrice } from "../../api/products";
import {
    Row,
    Modal,
    Form,
    ModalBody,
    ModalFooter,
    ModalHeader,
    Label,
    Input,
    FormFeedback,
    Button,
    Spinner
} from "reactstrap";
import { toast, ToastContainer } from "react-toastify";
import { useTranslation } from "react-i18next";
import "react-toastify/dist/ReactToastify.css";
import settings from "../../settings/settings";

export function SellerProducts() {
    const [modal, setModal] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [currentPrice, setCurrentPrice] = useState(null);
    const [deleteModal, setDeleteModal] = useState(false);
    const [loading, setLoading] = useState(false);
    const { sellerId } = useParams();
    const queryClient = useQueryClient();
    const { t, i18n } = useTranslation();
    const currentLang = i18n.language === "rs" ? "ru" : i18n.language;

    const getErrorMessage = (err) => {
        const errorData = err?.data?.error || err?.response?.data?.error;
        if (errorData?.message) {
            return t(errorData.message);
        }
        return err?.message ? t(err.message) : t("An error occurred");
    };

    const [page, setPage] = useState(1);
    const [size, setSize] = useState(10);
    const [searchTerm, setSearchTerm] = useState("");

    const topRef = useRef(null);
    const prevPage = useRef(page);

    const { data: sellerProductsData, isLoading, isError, error } = useQuery(getSellerProductsQuery(sellerId, { page, size }));

    useEffect(() => {
        if (page !== prevPage.current && !isLoading && topRef.current) {
            topRef.current.scrollIntoView({ behavior: "smooth" });
            prevPage.current = page;
        }
    }, [page, isLoading]);

    useEffect(() => {
        if (isError) {
            toast.error(getErrorMessage(error));
        }
    }, [isError, error]);

    const { data: searchedProductsData, isLoading: isSearching } = useQuery({
        ...getProductsQuery({ productName: searchTerm, size: 50, page: 1 }),
        enabled: modal && !isEdit,
    });

    const { data: addressesData } = useQuery({
        ...getSellerAddressesQuery(sellerId),
        enabled: modal // Fetch when modal is open
    });

    const products = useMemo(() => sellerProductsData?.data?.data || [], [sellerProductsData]);
    const searchedProducts = useMemo(() => searchedProductsData?.data?.data || [], [searchedProductsData]);
    const addressOptions = useMemo(() => {
        return addressesData?.data?.data?.map(addr => ({
            value: addr.id,
            label: `${addr.name} ${addr.address1 ? `- ${addr.address1}` : ''}`
        })) || [];
    }, [addressesData]);

    const toggle = () => {
        setModal(!modal);
        setSearchTerm("");
        if (modal) {
            setIsEdit(false);
            setCurrentPrice(null);
        }
    };

    const onDelete = () => {
        if (currentPrice) {
            deleteProductPrice(currentPrice.priceId)
                .then(() => {
                    queryClient.invalidateQueries({ queryKey: ["seller-products", sellerId] });
                    toast.success(t("Price deleted successfully"));
                    setDeleteModal(false);
                    setCurrentPrice(null);
                })
                .catch((err) => {
                    toast.error(getErrorMessage(err));
                    setDeleteModal(false);
                });
        }
    };

    const validation = useFormik({
        enableReinitialize: true,
        initialValues: {
            productId: currentPrice?.productId || "",
            price: currentPrice?.price || "",
            bazaarId: currentPrice?.bazaarId || 0,
            sellerAddressId: currentPrice?.sellerAddressId || "",
        },
        validationSchema: Yup.object({
            productId: Yup.string().required(t("Please select a product")),
            price: Yup.number().required(t("Please enter price")).min(0),
        }),
        onSubmit: (values, { resetForm }) => {
            if (isEdit && currentPrice) {
                const isPriceChanged = Number(values.price) !== Number(currentPrice.price);
                const isAddressChanged = values.sellerAddressId !== (currentPrice.sellerAddressId || "");

                if (!isPriceChanged && !isAddressChanged) {
                    toggle();
                    return;
                }
            }

            setLoading(true);
            const payload = {
                ...values,
                sellerId: sellerId,
                price: Number(values.price),
                bazaarId: Number(values.bazaarId),
                sellerAddressId: values.sellerAddressId,
            };

            if (isEdit) {
                editProductPrice(currentPrice.priceId, payload)
                    .then(() => {
                        queryClient.invalidateQueries({ queryKey: ["seller-products", sellerId] });
                        toast.success(t("Price updated successfully"));
                        resetForm();
                        toggle();
                    })
                    .catch((err) => {
                        toast.error(getErrorMessage(err));
                    })
                    .finally(() => setLoading(false));
            } else {
                addProductPrice(payload)
                    .then(() => {
                        queryClient.invalidateQueries({ queryKey: ["seller-products", sellerId] });
                        toast.success(t("Price added successfully"));
                        resetForm();
                        toggle();
                    })
                    .catch((err) => {
                        toast.error(getErrorMessage(err));
                    })
                    .finally(() => setLoading(false));
            }
        },
    });

    const productOptions = useMemo(() => {
        const options = searchedProducts.map(p => ({
            value: p.id,
            label: p.locales?.find(l => l.localeKey === currentLang)?.name || p.locales?.[0]?.name || p.normalizedName
        }));

        // In edit mode, the selected product might not be in the search results
        if (isEdit && currentPrice && !options.find(opt => opt.value === currentPrice.productId)) {
            options.push({
                value: currentPrice.productId,
                label: currentPrice.productNames?.find(l => l.localeKey === currentLang)?.name || currentPrice.normalizeName
            });
        }
        return options;
    }, [searchedProducts, isEdit, currentPrice, currentLang]);

    const columns = useMemo(
        () => [
            {
                header: t("Product"),
                accessorKey: "normalizeName",
                cell: (cell) => (
                    <div className="d-flex align-items-center">
                        <div className="flex-shrink-0 me-2">
                            <img
                                src={cell.row.original.imageId ? `${settings.catalogBaseURL}/products/images/${cell.row.original.imageId}` : "https://upload.wikimedia.org/wikipedia/commons/1/14/No_Image_Available.jpg"}
                                alt=""
                                className="avatar-xs rounded-circle"
                            />
                        </div>
                        <div className="flex-grow-1">
                            {cell.row.original.productNames?.find(l => l.localeKey === currentLang)?.name || cell.row.original.normalizeName}
                        </div>
                    </div>
                )
            },
            {
                header: t("Bazaar"),
                accessorKey: "address.bazaar",
                cell: (cell) => {
                    const bazaars = cell.getValue();
                    const bazaar = bazaars?.find(b => b.localeKey === currentLang) || bazaars?.[0];
                    return bazaar ? bazaar.title : "-";
                }
            },
            {
                header: t("Code"),
                accessorKey: "productCode",
            },
            {
                header: t("Price"),
                accessorKey: "price",
                cell: (cell) => (
                    <span className="text-success fw-bold">
                        {cell.getValue().toLocaleString()}
                    </span>
                )
            },
            {
                header: t("Unit"),
                accessorKey: "unitOfMeasure",
                cell: (cell) => <span className="text-capitalize">{t(cell.getValue())}</span>
            },
            {
                header: t("Actions"),
                cell: ({ row }) => (
                    <div className="d-flex gap-2">
                        <button
                            className="btn btn-sm btn-soft-primary"
                            onClick={() => {
                                setCurrentPrice(row.original);
                                setIsEdit(true);
                                setModal(true);
                            }}
                        >
                            <i className="ri-pencil-fill"></i>
                        </button>
                        <button
                            className="btn btn-sm btn-soft-danger"
                            onClick={() => {
                                setCurrentPrice(row.original);
                                setDeleteModal(true);
                            }}
                        >
                            <i className="ri-delete-bin-fill"></i>
                        </button>
                    </div>
                ),
            },
        ],
        [t]
    );

    if (isLoading) return <Loader />;

    return (
        <>
            <div className="card-body pt-0" style={{ paddingLeft: 0 }}>
                <DeleteModal
                    show={deleteModal}
                    onDeleteClick={onDelete}
                    onCloseClick={() => setDeleteModal(false)}
                />
                <Row className="g-4 mb-4 align-items-center">
                    <div className="col-sm">
                        <h5 ref={topRef} className="card-title mb-0">{t("Product Prices")}</h5>
                    </div>
                    <div className="col-sm-auto">
                        <Button color="success" onClick={toggle}>
                            <i className="ri-add-line align-bottom me-1"></i> {t("Add Price")}
                        </Button>
                    </div>
                </Row>

                {products.length > 0 ? (
                    <TableContainer
                        columns={columns}
                        data={products}
                        isAddUserList={false}
                        customPageSize={size}
                        className="custom-header-css"
                        pagination={{
                            page,
                            setPage,
                            total: sellerProductsData?.pagination?.TotalPages || 1,
                        }}
                    />
                ) : (
                    <div className="text-center p-3 text-muted">
                        <h5>{t("No Data")}</h5>
                    </div>
                )}

                <Modal isOpen={modal} toggle={toggle} centered>
                    <ModalHeader toggle={toggle}>
                        {isEdit ? t("Edit Price") : t("Add Price")}
                    </ModalHeader>
                    <Form
                        onSubmit={(e) => {
                            e.preventDefault();
                            validation.handleSubmit();
                        }}
                    >
                        <ModalBody>
                            <div className="mb-3">
                                <Label className="form-label">{t("Product")}</Label>
                                <Select
                                    name="productId"
                                    options={productOptions}
                                    isDisabled={isEdit}
                                    onInputChange={(value) => setSearchTerm(value)}
                                    isLoading={isSearching}
                                    value={productOptions.find(opt => opt.value === validation.values.productId)}
                                    onChange={(opt) => validation.setFieldValue("productId", opt.value)}
                                    placeholder={t("Select Product")}
                                />
                                {validation.errors.productId && validation.touched.productId ? (
                                    <div className="text-danger mt-1">{validation.errors.productId}</div>
                                ) : null}
                            </div>
                            <div className="mb-3">
                                <Label className="form-label">{t("Price")}</Label>
                                <Input
                                    name="price"
                                    type="number"
                                    placeholder={t("Enter price")}
                                    onChange={validation.handleChange}
                                    onBlur={validation.handleBlur}
                                    value={validation.values.price}
                                    invalid={validation.touched.price && validation.errors.price}
                                />
                                {validation.touched.price && validation.errors.price ? (
                                    <FormFeedback>{validation.errors.price}</FormFeedback>
                                ) : null}
                            </div>
                            <div className="mb-3">
                                <Label className="form-label">{t("Address")}</Label>
                                <Select
                                    name="sellerAddressId"
                                    options={addressOptions}
                                    value={addressOptions.find(opt => opt.value === validation.values.sellerAddressId)}
                                    onChange={(opt) => validation.setFieldValue("sellerAddressId", opt.value)}
                                    placeholder={t("Select Address")}
                                />
                            </div>
                        </ModalBody>
                        <ModalFooter>
                            <Button color="light" onClick={toggle}>{t("Close")}</Button>
                            <Button color="success" type="submit" disabled={loading}>
                                {loading ? <Spinner size="sm" className="me-2" /> : null}
                                {isEdit ? t("Update") : t("Add")}
                            </Button>
                        </ModalFooter>
                    </Form>
                </Modal>
                <ToastContainer closeButton={false} limit={1} />
            </div>
        </>
    );
}
